import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { noShowReasonLabel } from '../../lib/noShowReasons';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalAnimalListMini from '../../components/dyrejournal/listAnimalMini';
import DyrejournalPrescriptionListMini from '../../components/dyrejournal/listPrescriptionMini';
import DyrejournalJournalListMini from '../../components/dyrejournal/listJournalMini';
import Versions from '../../components/versions';
import { animalAge } from '../../lib/animalAge';

@observer
class ViewAnimal extends Component {
	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('animal', 'Animal basics'),
            sectionsInsurance: fields.get('animal', 'Animal insurance'),
            sectionsMedia: fields.get('animal', 'Animal media'),
            calendarEvents: [],
        };
        this.searchTimer = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id } = props;
        const { journalStore, animalStore } = this.props.stores;
        await this.loadAnimal(id);

        const { animal } = animalStore;
        await this.loadSpecies();
        await this.loadBreeds(animal?.speciesId);
    }

    loadSpecies = async () => {
        const { animalStore } = this.props.stores;
        await animalStore.loadSpecies();
    }

    loadBreeds = async speciesId => {
        const { animalStore } = this.props.stores;
        await animalStore.loadBreeds(speciesId);
    }

    loadAnimal = async id => {
        const { animalStore } = this.props.stores;
        await animalStore.load(id, false, { addData: ['owners'] });
        const animalIds = [id];
        await Promise.all([
            this.loadJournals(animalIds),
            this.loadCalendarEvents(animalIds),
        ]);
    }

    loadJournals = async animalIds => {
        const { journalStore, saleStore } = this.props.stores;
        journalStore.updateKeyValue('journals', []);
        if (animalIds) {
            const response = await journalStore.load({
                query: {
                    animal: animalIds,
                },
            });
        }
    }

    loadCalendarEvents = async animalIds => {
        const { calendarEventStore } = this.props.stores;
        if (!animalIds || animalIds.length === 0) {
            this.setState({ calendarEvents: [] });
            return;
        }
        const events = await calendarEventStore.load({
            query: { animal: animalIds },
            skipUpdate: 1,
        });
        this.setState({ calendarEvents: Array.isArray(events) ? events : [] });
    }

    openCalendarEvent = (id) => () => {
        const { drawerLevel = 1 } = this.props;
        const { appState, calendarStore } = this.props.stores;
        const { eventId } = calendarStore;
        if (eventId) return;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewCalendarEvent', {
            height: drawerHeightMedium,
            id: parseInt(id, 10),
            showEdit: true,
            callback: () => {
                appState.toggleDrawer(false, drawerLevel + 2);
                calendarStore.resetDragging();
                this.loadCalendarEvents([this.props.id]);
            },
        }, drawerLevel + 1);
    }

    createNewVisit = () => {
        const { drawerLevel } = this.props;
        const { appState, userStore, animalStore, saleStore } = this.props.stores;
        const { animal, owners } = animalStore;
        const owner = owners.find(e => e.id === animal.owner);

        saleStore.updateObjectKeyValue('newSale', 'visitors', [owner]);
        saleStore.updateObjectKeyValue('newSale', 'animals', [animal]);
        route(`/dyrejournal-sale/`);
        appState.toggleDrawer(drawerLevel);
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillUnmount() {
        // When the drawer closes, push the latest animal record back into the
        // parent sale state. Without this, saleStore.newSale.animals[idx] keeps
        // the data the sale was opened with — so freshly-saved fields (e.g.
        // gender) appear lost on the visit screen even though the animal
        // record is correct in the database.
        const { callback } = this.props;
        if (typeof callback === 'function') {
            callback();
        }
    }

    renderCalendarSection({ title, icon, events, emptyKey, emptyDefault, isNotification }) {
        if (!events || events.length === 0) return null;
        const sorted = [...events].sort((a, b) => new Date(b.start) - new Date(a.start));
        return (
            <div class='mt-3 mx-1'>
                <div class='font-weight-lighter px-3 py-1 text-body-secondary'>
                    <small class='text-uppercase'>
                        <i class={`${icon} me-1`} />
                        {title}
                    </small>
                </div>
                <div class='table-responsive'>
                    <small>
                        <table class='table table-striped table-sm mb-0'>
                            <thead>
                                <tr>
                                    <th scope='col'>&nbsp;</th>
                                    <th scope='col'><Text id='dyrejournal.calendar-event.date'>Date</Text></th>
                                    <th scope='col'><Text id='dyrejournal.calendar-event.title'>Title</Text></th>
                                    <th scope='col'><Text id='dyrejournal.calendar-event.status'>Status</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map(ev => {
                                    const start = ev.start ? new Date(ev.start) : null;
                                    const inPast = start && start.getTime() < Date.now();
                                    const smsSent = ev.notificationSMSSent || ev.reminderSMSSent || ev.autoReminderSMSSent;
                                    return (
                                        <tr
                                            key={ev.id}
                                            style='cursor: pointer;'
                                            onClick={this.openCalendarEvent(ev.id)}
                                        >
                                            <td><i class='fas fa-edit' /></td>
                                            <td>
                                                {start ? util.formatDate(start, { day: 'numeric', month: 'short', year: 'numeric', locale: 'nb-NO' }) : ''}
                                                {start && !isNotification && (
                                                    <span class='ms-1 text-body-secondary'>{util.isoTime(start)}</span>
                                                )}
                                            </td>
                                            <td>{ev.title}</td>
                                            <td>
                                                {ev.skipReminders && (
                                                    <span class='badge bg-secondary me-1'>
                                                        <Text id='notifications.skipped'>Skipped</Text>
                                                    </span>
                                                )}
                                                {ev.didNotShow && (
                                                    <span class='badge text-bg-danger me-1' title={ev.didNotShowNote || ''}>
                                                        <Text id='dyrejournal.no-show.badge'>No-show</Text>
                                                    </span>
                                                )}
                                                {ev.didNotShow && ev.didNotShowReason && (
                                                    <small class='text-body-secondary me-1'>{noShowReasonLabel(ev.didNotShowReason)}</small>
                                                )}
                                                {ev.didNotShow && smsSent && (
                                                    <span class='badge bg-success'>
                                                        <Text id='notifications.sent'>Sent</Text>
                                                    </span>
                                                )}
                                                {!ev.didNotShow && (smsSent
                                                    ? <span class='badge bg-success'>
                                                        <Text id='notifications.sent'>Sent</Text>
                                                    </span>
                                                    : inPast
                                                        ? <span class='badge bg-light text-dark'>
                                                            <Text id='dyrejournal.calendar-event.past'>Past</Text>
                                                        </span>
                                                        : <span class='badge bg-info text-dark'>
                                                            <Text id='notifications.pending'>Pending</Text>
                                                        </span>)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </small>
                </div>
            </div>
        );
    }

    render() {
        const {
            week,
            isNew,
            hideAdmin = true,
            drawerLevel = 1,
            showJournals = true,
        } = this.props;
        const {
            sections,
            sectionsInsurance,
            sectionsMedia,
            calendarEvents,
        } = this.state;
        const { userStore, journalStore, animalStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { animal = {} } = animalStore;

        const { journals } = journalStore;

        const notifications = calendarEvents.filter(e => e.calendarId === 'Innkallinger');
        const bookings = calendarEvents.filter(e => e.calendarId !== 'Innkallinger');

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <Versions stores={this.props.stores} type='animal' id={animal?.id} key={`animal-${animal.id}`} />

                    <div class='font-weight-lighter px-3 box-header'>
                        <Text id='dyrejournal.animal.view'>View Animal</Text> {animal?.id && <>: {animal.id}: {animal.name} ({animal.species}/{animal.breed}) {animal.birth ? animalAge(animal.birth) : ''}</>}
                    </div>

                    {animal?.isLost && <div class='alert alert-danger mx-3 my-2 d-flex align-items-center'>
                        <i class='fa-solid fa-triangle-exclamation me-2 fa-lg' />
                        <div>
                            <strong><Text id='dyrejournal.animal.isLost-banner'>This animal is reported lost!</Text></strong>
                            <div><small><Text id='dyrejournal.animal.isLost-help'>Owner has marked this animal as lost. Verify identity before any treatment.</Text></small></div>
                        </div>
                    </div>}

                    <DyrejournalJournalListMini
                        classNames='mx-1'
                        stores={this.props.stores}
                        journals={journals}
                        drawerLevel={drawerLevel}
                        collapse={true}
                    />

                    {this.renderCalendarSection({
                        title: <Text id='dyrejournal.animal.notifications'>Notifications</Text>,
                        icon: 'fa-duotone fa-bell',
                        events: notifications,
                        isNotification: true,
                    })}

                    {this.renderCalendarSection({
                        title: <Text id='dyrejournal.animal.bookings'>Bookings</Text>,
                        icon: 'fa-duotone fa-calendar',
                        events: bookings,
                        isNotification: false,
                    })}

                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sections}
                            holdingStore={animalStore}
                            objectName={isNew ? 'newAnimal' : 'animal'}
                            isNew={isNew}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                        />
                    </div>

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsInsurance}
                            holdingStore={animalStore}
                            objectName={isNew ? 'newAnimal' : 'animal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                        />
                    </div>

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsMedia}
                            holdingStore={animalStore}
                            objectName={isNew ? 'newAnimal' : 'animal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                        />
                    </div>

                    <div class='w-100 d-flex flex-row justify-content-center mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                        <button type='button' class='btn btn-primary rounded-pill mx-2' onClick={this.createNewVisit}>
                            <Text id='dyrejournal.create-new-visit'>Create new visit</Text>
                        </button>
                    </div>

                </div>
            </div>
        );
    }
}

export default ViewAnimal;
